/**
 * GranolaIntegrationSettings
 *
 * Settings card for Granola AI direct API integration.
 * Users enter their Granola API key (grn_*) to connect.
 * The system polls every 15 min for new meeting transcripts.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Check,
  Clock,
  Key,
  Loader2,
  Mic2,
  RefreshCw,
  Unplug,
} from 'lucide-react';
import { useGranolaIntegration } from '@/hooks/useGranolaIntegration';

export function GranolaIntegrationSettings() {
  const {
    status,
    loadingStatus,
    saving,
    syncing,
    unassignedCount,
    connect,
    disconnect,
    syncNow,
  } = useGranolaIntegration();

  const [apiKey, setApiKey] = useState('');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const handleConnect = async () => {
    if (!apiKey.trim()) return;
    const success = await connect(apiKey.trim());
    if (success) {
      setApiKey('');
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    setShowDisconnectConfirm(false);
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Mic2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Granola AI Meeting Notes</p>
              <p className="text-xs text-muted-foreground mt-1">
                Connect your Granola account to automatically import meeting transcripts,
                summaries, and action items. AI classifies each meeting to the correct deal.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mic2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">
                {status.configured ? 'Granola AI Connected' : 'Connect Granola AI'}
              </CardTitle>
              <CardDescription>
                {status.configured
                  ? 'Meeting notes sync automatically every 15 minutes'
                  : 'Enter your Granola API key to get started'
                }
              </CardDescription>
            </div>
            {status.configured && (
              <Badge
                variant="outline"
                className={status.enabled
                  ? 'text-green-600 border-green-300 bg-green-50'
                  : 'text-muted-foreground border-border'
                }
              >
                {status.enabled ? (
                  <><Check className="h-3 w-3 mr-1" /> Connected</>
                ) : (
                  'Paused'
                )}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Not Connected — API Key Input */}
          {!status.configured && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Key className="h-3.5 w-3.5" />
                  Granola API Key
                </label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="grn_..."
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                    className="flex-1 font-mono"
                  />
                  <Button
                    onClick={handleConnect}
                    disabled={saving || !apiKey.startsWith('grn_')}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {saving ? 'Validating...' : 'Connect'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Find your API key in Granola → Settings → API. Keys start with "grn_".
                </p>
              </div>

              {/* Help text */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">How it works:</p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>Enter your Granola API key above</li>
                  <li>We validate and securely encrypt your key</li>
                  <li>Every 15 minutes, we fetch your new meeting notes</li>
                  <li>AI automatically classifies each meeting to the right deal</li>
                  <li>Transcripts appear in project documents with full searchability</li>
                </ol>
              </div>
            </div>
          )}

          {/* Connected — Stats & Actions */}
          {status.configured && (
            <>
              {/* Error Banner */}
              {status.lastError && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-destructive">Sync Error</p>
                    <p className="text-xs text-destructive/80 mt-0.5">{status.lastError}</p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold tabular-nums">{status.totalIngested || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total Imported</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold tabular-nums">{status.recentCount || 0}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Last 7 Days</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className={`text-2xl font-bold tabular-nums ${(status.unassignedCount || 0) > 0 ? 'text-amber-600' : ''}`}>
                    {status.unassignedCount || unassignedCount}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Unassigned</p>
                </div>
              </div>

              {/* Last Synced */}
              {status.lastSyncedAt && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Last synced: {(status.lastSyncedAt as Date).toLocaleString()}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={syncNow}
                  disabled={syncing}
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>

                <div className="flex-1" />

                {showDisconnectConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Are you sure?</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDisconnect}
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes, Disconnect'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDisconnectConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Unplug className="h-4 w-4 mr-1" />
                    Disconnect
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
